import jQuery from 'jquery';
import {
	fetchFilteredGridPosts,
	portfolioCardTemplate,
	exitHubFilteredMode,
} from './grid-filter-ajax';
import { setHubFilterUrl } from '../vendor/url-helper-functions';

const DEBOUNCE_MS = 350;
const MIN_QUERY_LENGTH = 2;

let feedbackEl;
let statusEl;
let emptyEl;
let emptyTermEl;
let emptyCopyDefaultEl;
let emptyCopyWidenEl;
let emptyCopyWidenTitleEl;
let originalWidenTitle = '';
let fullSearchLinkEl;
let resetFilterBarEl;
let searchFormEl;
let searchInput;
let didYouMeanEl;
let didYouMeanLinksEl;
let furtherWrapEl;
let originalFurtherHtml = null;
let requestSeq = 0;
let searchLoading = false;

const PLACEHOLDER_IDLE = '';
const PLACEHOLDER_FOCUS = 'Search';

export function getPortfolioHubSearchTerm() {
	if (!searchInput) {
		const input = document.querySelector(
			'#select-and-search-wrapper input[name="searchfilter"][value="case-study"]'
		);
		const form = input ? input.closest('form') : null;
		const field = form ? form.querySelector('input[name="s"]') : null;
		return field ? field.value.trim() : '';
	}
	return searchInput.value.trim();
}

function cleanLabel(value) {
	return String(value || '')
		.replace(/\s+/g, ' ')
		.trim();
}

export function getPortfolioHubSelectedFilter() {
	const selected = document.querySelector('#multi-filter-wrapper .child-filter.is-selected');
	if (!selected) {
		return { term: 'all', taxonomy: 'all', value: 'all', filterby: 'all', label: '', group: '' };
	}
	const value = selected.getAttribute('data-value') || 'all';
	const filterby = selected.getAttribute('data-filterby') || 'all';
	const termText = selected.querySelector('.term-text');
	const label = cleanLabel(termText ? termText.textContent : selected.textContent);

	// Term names repeat across filters ("West Midlands" is both Location and LDC
	// Team), so the group heading is what makes the message unambiguous.
	const group = cleanLabel(
		selected.closest('.child-filters')?.querySelector('.title')?.textContent
	);

	return { term: value, taxonomy: filterby, value, filterby, label, group };
}

function filterScopeLabel(filter) {
	if (!filter || !filter.label) {
		return '';
	}
	return filter.group ? `${filter.group}: ${filter.label}` : filter.label;
}

function searchQueryForMessage(rawQuery) {
	return rawQuery.trim().replace(/"/g, '\u2019');
}

function escapeHtml(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function getGrid() {
	return jQuery('.grid-for-filtering[data-post-type="case-study"]');
}

function rememberOriginalGrid() {
	if (originalFurtherHtml !== null || !furtherWrapEl) {
		return;
	}
	originalFurtherHtml = furtherWrapEl.innerHTML;
}

function restoreOriginalGrid() {
	if (!furtherWrapEl || originalFurtherHtml === null) {
		return;
	}
	furtherWrapEl.innerHTML = originalFurtherHtml;
	exitHubFilteredMode();
}

function ensureSearchLoader() {
	if (!searchFormEl) {
		return;
	}
	if (!searchFormEl.querySelector('.hub-filter-loader')) {
		const loader = document.createElement('span');
		loader.className = 'hub-filter-loader';
		loader.setAttribute('aria-hidden', 'true');
		const wrap = searchFormEl.querySelector('.search-field-wrap') || searchFormEl;
		wrap.appendChild(loader);
	}
}

function setSearchLoading(isLoading) {
	searchLoading = Boolean(isLoading);
	ensureSearchLoader();
	if (!searchFormEl) {
		return;
	}
	searchFormEl.classList.toggle('is-filter-loading', searchLoading);
	searchFormEl.setAttribute('aria-busy', searchLoading ? 'true' : 'false');
}

function updateFullSearchLink(rawQuery) {
	const q = rawQuery.trim();
	if (!q || !searchFormEl || !fullSearchLinkEl) {
		return;
	}
	const u = new URL(searchFormEl.action || window.location.origin);
	u.searchParams.set('searchfilter', 'case-study');
	u.searchParams.set('s', q);
	fullSearchLinkEl.setAttribute('href', u.toString());
}

function hideDidYouMean() {
	if (didYouMeanEl) {
		didYouMeanEl.hidden = true;
	}
	if (didYouMeanLinksEl) {
		didYouMeanLinksEl.innerHTML = '';
	}
}

function renderDidYouMean(suggestions) {
	if (!didYouMeanEl || !didYouMeanLinksEl) {
		return;
	}
	if (!Array.isArray(suggestions) || !suggestions.length) {
		hideDidYouMean();
		return;
	}

	didYouMeanLinksEl.innerHTML = suggestions
		.map((name, index) => {
			const safe = escapeHtml(name);
			const sep =
				index > 0
					? `<span class="team-hub-search-feedback__did-you-mean-sep" aria-hidden="true">${
							index === suggestions.length - 1 ? ' or ' : ', '
						}</span>`
					: '';
			return `${sep}<button type="button" class="team-hub-search-feedback__did-you-mean-link" data-suggestion="${safe}">${safe}</button>`;
		})
		.join('');
	didYouMeanEl.hidden = false;
}

function setStatusMessage(message) {
	if (!statusEl) {
		return;
	}
	statusEl.textContent = message;
	// The status truncates on one line at desktop; keep the full text reachable.
	if (message) {
		statusEl.setAttribute('title', message);
	} else {
		statusEl.removeAttribute('title');
	}
}

function setBarResetVisible(visible) {
	if (resetFilterBarEl) {
		resetFilterBarEl.hidden = !visible;
	}
}

function formatMatchMessage(visibleCount, rawQuery, scopeLabel) {
	const quoted = searchQueryForMessage(rawQuery);
	let countBit;
	if (visibleCount === 0) {
		countBit = `No companies match “${quoted}”`;
	} else if (visibleCount === 1) {
		countBit = `1 company matches “${quoted}”`;
	} else {
		countBit = `${visibleCount} companies match “${quoted}”`;
	}

	if (!scopeLabel) {
		return `${countBit} across all companies`;
	}

	return `${countBit} in ${scopeLabel}`;
}

function updateSearchFeedback(rawQuery, options = {}) {
	if (!feedbackEl || !statusEl || !emptyEl) {
		return;
	}

	const q = rawQuery.trim();
	const {
		errorMessage,
		visibleCount = 0,
		suggestWidenFilter = false,
		didYouMean = [],
		scopeLabel = '',
		filterActive = false,
	} = options;

	if (errorMessage) {
		feedbackEl.hidden = false;
		emptyEl.hidden = true;
		if (emptyCopyDefaultEl) {
			emptyCopyDefaultEl.hidden = false;
		}
		if (emptyCopyWidenEl) {
			emptyCopyWidenEl.hidden = true;
		}
		setBarResetVisible(false);
		setStatusMessage(errorMessage);
		hideDidYouMean();
		return;
	}

	if (!q) {
		feedbackEl.hidden = true;
		setStatusMessage('');
		emptyEl.hidden = true;
		if (emptyCopyDefaultEl) {
			emptyCopyDefaultEl.hidden = false;
		}
		if (emptyCopyWidenEl) {
			emptyCopyWidenEl.hidden = true;
		}
		setBarResetVisible(false);
		hideDidYouMean();
		return;
	}

	if (visibleCount === 0) {
		// Keep the count line up: it names the filter the search ran in, which is
		// what makes "Search all companies" next to it meaningful.
		feedbackEl.hidden = false;
		setStatusMessage(formatMatchMessage(0, rawQuery, scopeLabel));
		emptyEl.hidden = false;
		const widen = Boolean(suggestWidenFilter);
		if (emptyCopyDefaultEl) {
			emptyCopyDefaultEl.hidden = widen;
		}
		if (emptyCopyWidenEl) {
			emptyCopyWidenEl.hidden = !widen;
		}
		setBarResetVisible(filterActive);
		if (emptyCopyWidenTitleEl) {
			emptyCopyWidenTitleEl.textContent =
				widen && scopeLabel
					? `No companies in ${scopeLabel} match your search.`
					: originalWidenTitle;
		}
		if (emptyTermEl) {
			emptyTermEl.textContent = q;
		}
		updateFullSearchLink(rawQuery);
		renderDidYouMean(Array.isArray(didYouMean) ? didYouMean : []);
		return;
	}

	emptyEl.hidden = true;
	setBarResetVisible(filterActive);
	feedbackEl.hidden = false;
	setStatusMessage(formatMatchMessage(visibleCount, rawQuery, scopeLabel));
	hideDidYouMean();
}

export function handlePortfolioHubGridResult(result, meta = {}) {
	if (meta.append) {
		return;
	}

	const query = getPortfolioHubSearchTerm();
	if (!query || query.length < MIN_QUERY_LENGTH) {
		return;
	}

	if (result.error === 'rate_limited') {
		updateSearchFeedback(query, {
			errorMessage:
				result.message || 'Too many searches. Please wait a moment and try again.',
		});
		return;
	}

	const visibleCount = result.matched_total || (result.posts ? result.posts.length : 0);
	const filter = getPortfolioHubSelectedFilter();
	const filterActive = filter.term !== 'all';
	const suggestWidenFilter =
		visibleCount === 0 && result.unfiltered_match_count > 0 && filterActive;

	updateSearchFeedback(query, {
		visibleCount,
		suggestWidenFilter,
		didYouMean: visibleCount === 0 ? result.suggestions : [],
		scopeLabel: filterActive ? filterScopeLabel(filter) : '',
		filterActive,
	});
}

function searchFetchOptions(query) {
	const filter = getPortfolioHubSelectedFilter();
	return {
		postType: 'case-study',
		term: filter.term,
		taxonomy: filter.taxonomy,
		filterby: filter.filterby,
		value: filter.value,
		$grid: getGrid(),
		cardTemplate: portfolioCardTemplate,
		instantFilter: false,
		searchTerm: query,
		emptyHtml: '',
		onResult: handlePortfolioHubGridResult,
	};
}

export function applyPortfolioHubSearch(rawQuery) {
	const query = rawQuery.trim();
	const seq = ++requestSeq;

	rememberOriginalGrid();

	if (!query) {
		setSearchLoading(false);
		const filter = getPortfolioHubSelectedFilter();
		if (filter.term !== 'all') {
			fetchFilteredGridPosts({
				postType: 'case-study',
				term: filter.term,
				taxonomy: filter.taxonomy,
				filterby: filter.filterby,
				value: filter.value,
				$grid: getGrid(),
				cardTemplate: portfolioCardTemplate,
				instantFilter: false,
				searchTerm: '',
			});
		} else {
			restoreOriginalGrid();
		}
		updateSearchFeedback(rawQuery);
		return;
	}

	if (query.length < MIN_QUERY_LENGTH) {
		setSearchLoading(false);
		const filter = getPortfolioHubSelectedFilter();
		if (filter.term !== 'all') {
			fetchFilteredGridPosts({
				postType: 'case-study',
				term: filter.term,
				taxonomy: filter.taxonomy,
				filterby: filter.filterby,
				value: filter.value,
				$grid: getGrid(),
				cardTemplate: portfolioCardTemplate,
				instantFilter: false,
				searchTerm: '',
			});
		} else {
			restoreOriginalGrid();
		}
		if (feedbackEl && statusEl) {
			feedbackEl.hidden = false;
			setStatusMessage('Keep typing to search…');
		}
		if (emptyEl) {
			emptyEl.hidden = true;
		}
		setBarResetVisible(false);
		hideDidYouMean();
		return;
	}

	setSearchLoading(true);
	if (feedbackEl && statusEl) {
		feedbackEl.hidden = false;
		setStatusMessage('Searching…');
	}
	if (emptyEl) {
		emptyEl.hidden = true;
	}
	setBarResetVisible(false);
	hideDidYouMean();

	const request = fetchFilteredGridPosts(searchFetchOptions(query));
	if (request && typeof request.fail === 'function') {
		request.fail((xhr, status) => {
			if (status === 'abort' || seq !== requestSeq) {
				return;
			}
			updateSearchFeedback(query, {
				errorMessage: 'We could not run the search just now. Please try again.',
			});
		});
	}
	if (request && typeof request.always === 'function') {
		request.always(() => {
			if (seq === requestSeq) {
				setSearchLoading(false);
			}
		});
	} else {
		setSearchLoading(false);
	}
}

export function resetPortfolioHubFilterToAll() {
	const $parentFilters = jQuery('#multi-filter-wrapper .parent-filters');
	const $all = $parentFilters.find('.all-filter');
	const $select = jQuery('#multi-filter .select');

	$parentFilters.find('*').removeClass('is-active is-selected');
	$all.addClass('is-selected');
	if ($select.length && $all.length) {
		$select.text($all.text());
	}

	setHubFilterUrl('all');
	applyPortfolioHubSearch(getPortfolioHubSearchTerm());
}

function applyDidYouMeanSuggestion(name) {
	if (!searchInput || !name) {
		return;
	}
	searchInput.value = name;
	searchInput.focus();
	searchInput.dispatchEvent(new Event('input', { bubbles: true }));
	applyPortfolioHubSearch(name);
}

function debounce(fn, ms) {
	let t;
	return function debounced() {
		clearTimeout(t);
		t = setTimeout(fn, ms);
	};
}

function init() {
	const filterField = document.querySelector(
		'#select-and-search-wrapper input[name="searchfilter"][value="case-study"]'
	);
	const form = filterField ? filterField.closest('form') : null;
	if (!form || !document.querySelector('.grid-for-filtering[data-post-type="case-study"]')) {
		return;
	}

	searchFormEl = form;
	ensureSearchLoader();
	feedbackEl = document.getElementById('portfolio-hub-search-feedback');
	statusEl = document.getElementById('portfolio-hub-search-status');
	emptyEl = document.getElementById('portfolio-hub-search-empty');
	emptyTermEl = document.getElementById('portfolio-hub-search-empty-term');
	emptyCopyDefaultEl = document.getElementById('portfolio-hub-empty-copy-default');
	emptyCopyWidenEl = document.getElementById('portfolio-hub-empty-copy-widen');
	emptyCopyWidenTitleEl = document.getElementById('portfolio-hub-empty-copy-widen-title');
	originalWidenTitle = emptyCopyWidenTitleEl ? emptyCopyWidenTitleEl.textContent.trim() : '';
	fullSearchLinkEl = document.getElementById('portfolio-hub-full-search-link');
	resetFilterBarEl = document.getElementById('portfolio-hub-reset-filter-bar');
	didYouMeanEl = document.getElementById('portfolio-hub-did-you-mean');
	didYouMeanLinksEl = document.getElementById('portfolio-hub-did-you-mean-links');
	furtherWrapEl = document.querySelector('.further-case-studies');

	const input = form.querySelector('input[name="s"]');
	if (!input) {
		return;
	}

	searchInput = input;
	rememberOriginalGrid();

	searchInput.addEventListener('focus', () => {
		searchInput.placeholder = PLACEHOLDER_FOCUS;
	});
	searchInput.addEventListener('blur', () => {
		searchInput.placeholder = PLACEHOLDER_IDLE;
	});

	const runDebounced = debounce(() => {
		applyPortfolioHubSearch(searchInput.value);
	}, DEBOUNCE_MS);

	searchInput.addEventListener('input', () => {
		if (!searchInput.value.trim()) {
			requestSeq += 1;
			applyPortfolioHubSearch('');
		} else {
			if (feedbackEl && statusEl) {
				feedbackEl.hidden = false;
				setStatusMessage('Searching…');
			}
			runDebounced();
		}
	});

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		applyPortfolioHubSearch(searchInput.value);
	});

	if (didYouMeanLinksEl) {
		didYouMeanLinksEl.addEventListener('click', (e) => {
			const button = e.target.closest('.team-hub-search-feedback__did-you-mean-link');
			if (!button) {
				return;
			}
			e.preventDefault();
			applyDidYouMeanSuggestion(
				button.getAttribute('data-suggestion') || button.textContent || ''
			);
		});
	}

	if (resetFilterBarEl) {
		resetFilterBarEl.addEventListener('click', () => {
			resetPortfolioHubFilterToAll();
		});
	}

	applyPortfolioHubSearch(searchInput.value);
}

jQuery(init);
